<template>
  <div class="w-full rounded-xl border border-gray-200 bg-white overflow-hidden">
    <div v-if="loading" class="p-3 flex flex-col gap-2">
      <Skeleton width="40%" height="0.9rem" borderRadius="0.4rem" />
      <Skeleton width="100%" height="2.2rem" borderRadius="0.6rem" />
    </div>

    <template v-else>
      <!-- Current state -->
      <div class="flex items-center gap-3 px-3 py-3">
        <span class="w-10 h-10 rounded-full flex items-center justify-center flex-none" :class="state.tileClass">
          <i :class="['pi', state.icon, '!text-sm']" />
        </span>
        <div class="flex-1 min-w-0">
          <div class="text-[12px] text-gray-500">חובות ויתרות</div>
          <div class="text-[15px] font-semibold" :class="state.textClass">{{ state.label }}</div>
        </div>
        <Button
          v-if="balance < 0 && canPayDebt"
          label="תשלום עכשיו"
          size="small"
          @click="payDebt"
        />
      </div>

      <!-- Actions -->
      <div v-if="canEdit" class="flex gap-2 px-3 pb-3">
        <Button label="רישום חוב" icon="pi pi-minus" size="small" outlined severity="danger" class="flex-1" @click="openAddEntry('debt')" />
        <Button label="רישום יתרה" icon="pi pi-plus" size="small" outlined severity="success" class="flex-1" @click="openAddEntry('credit')" />
      </div>

      <!-- History (view only) -->
      <template v-if="history.length">
        <button
          type="button"
          class="w-full flex items-center justify-between px-3 py-2 border-t border-gray-100 text-[12.5px] text-gray-600"
          @click="showHistory = !showHistory"
        >
          <span>היסטוריה ({{ history.length }})</span>
          <i :class="['pi', showHistory ? 'pi-chevron-up' : 'pi-chevron-down', '!text-xs']" />
        </button>
        <div v-if="showHistory" class="border-t border-gray-100">
          <div
            v-for="entry in history"
            :key="entry.id"
            class="flex items-start gap-2.5 px-3 py-2 border-b border-gray-50 last:border-b-0"
          >
            <div class="flex-1 min-w-0">
              <div class="text-[13px] font-medium">{{ typeLabel(entry.type) }}</div>
              <div v-if="entry.note" class="text-[11.5px] text-gray-500 truncate">{{ entry.note }}</div>
              <div class="text-[11px] text-gray-400">
                {{ formatDate(entry.createdAt) }}<template v-if="entry.createdBy?.name"> · {{ entry.createdBy.name }}</template>
              </div>
            </div>
            <div class="flex flex-col items-end flex-none">
              <span class="text-[13px] font-semibold" :class="entry.amount < 0 ? 'text-red-500' : 'text-green-600'" style="direction: ltr">
                {{ entry.amount < 0 ? '−' : '+' }}{{ ils(Math.abs(entry.amount)) }}
              </span>
              <span class="text-[10.5px] text-gray-400">יתרה: {{ signedLabel(entry.balanceAfter) }}</span>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import Skeleton from 'primevue/skeleton';
import {
  CustomerBalanceTransactionTypeEnum,
  ICustomerBalanceResponse,
  ICustomerBalanceTransaction
} from '@torim-app/types';
import { useDrawer } from '@torim-app/shared-components';
import { CustomersApi } from '@app/api/admin';
import { useAdminStore } from '@app/stores';
import { trackEvent } from '@app/composables/posthog';
import AddBalanceEntryDrawer from '@app/components/admin/pages/customer/AddBalanceEntryDrawer.vue';
import PaymentOptions from '@app/components/admin/modals/PaymentOptions.vue';

const props = defineProps<{ customerId: string }>();
// Fired after anything that may have produced a payment, so the payments list reloads.
const emit = defineEmits<{ changed: [] }>();

const toast = useToast();
const adminStore = useAdminStore();
const { openDrawer, closeDrawer } = useDrawer();

const loading = ref(true);
const balance = ref(0);
const history = ref<ICustomerBalanceTransaction[]>([]);
const showHistory = ref(false);

const canEdit = computed(() => adminStore.getCanEdit);
// "Pay now" needs something to settle through: a payment link or an invoice.
const canPayDebt = computed(() => canEdit.value
  && !!(adminStore.businessSiteData?.hasPayments || adminStore.businessSiteData?.hasInvoices));

const ils = (n: number) => '₪' + (n ?? 0).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const signedLabel = (n: number) => (n < 0 ? `חוב ${ils(-n)}` : ils(n));

const state = computed(() => {
  if (balance.value < 0)
    return { label: `חוב של ${ils(-balance.value)}`, icon: 'pi-exclamation-circle', tileClass: 'bg-red-50 text-red-500', textClass: 'text-red-600' };
  if (balance.value > 0)
    return { label: `יתרת זכות של ${ils(balance.value)}`, icon: 'pi-wallet', tileClass: 'bg-green-50 text-green-600', textClass: 'text-green-700' };
  return { label: 'אין חוב או יתרה', icon: 'pi-check-circle', tileClass: 'bg-gray-100 text-gray-400', textClass: 'text-gray-700' };
});

const typeLabel = (type: CustomerBalanceTransactionTypeEnum): string => ({
  [CustomerBalanceTransactionTypeEnum.DebtAdded]: 'נרשם חוב',
  [CustomerBalanceTransactionTypeEnum.CreditAdded]: 'נרשמה יתרה',
  [CustomerBalanceTransactionTypeEnum.DebtPayment]: 'תשלום חוב',
  [CustomerBalanceTransactionTypeEnum.CreditUsed]: 'שימוש ביתרה',
  [CustomerBalanceTransactionTypeEnum.DebtReinstated]: 'החזר תשלום חוב',
  [CustomerBalanceTransactionTypeEnum.CreditRestored]: 'החזר יתרה',
}[type] ?? type);

const formatDate = (date?: Date | string) =>
  date ? DateTime.fromISO(String(date)).setLocale('he').toFormat('dd/MM/yy HH:mm') : '';

const apply = (res: ICustomerBalanceResponse) => {
  balance.value = res.balance;
  history.value = res.history;
};

const load = async () => {
  try {
    apply(await CustomersApi.getBalance(props.customerId));
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון את החובות והיתרות');
  } finally {
    loading.value = false;
  }
};

const openAddEntry = (kind: 'debt' | 'credit') => {
  trackEvent('customer_balance_add_clicked', { kind });
  openDrawer(AddBalanceEntryDrawer, {
    customerId: props.customerId,
    initialKind: kind,
    onSaved: apply,
  }, { title: kind === 'debt' ? 'רישום חוב' : 'רישום יתרה' });
};

const payDebt = () => {
  trackEvent('customer_debt_pay_clicked');
  openDrawer(PaymentOptions, {
    uuid: props.customerId,
    payFor: 'debt',
    debtAmount: -balance.value,
    onClose: (shouldLoad: boolean) => {
      closeDrawer();
      if (shouldLoad) {
        emit('changed');
        // A link payment settles when the customer pays; an invoice settles right away.
        load();
      }
    }
  }, { title: 'תשלום חוב' });
};

onMounted(load);

</script>
